import React, { useEffect, useState, useRef } from 'react';
import { RichTextBlock, RichText } from 'prismic-reactjs';
import Cookies from 'js-cookie';
import getLinkConfig from '@helpers/get-link-config';
import {
  TickerContainer,
  TickerContent,
  TickerMessage,
  TickerLink,
  CloseButton,
} from './ticker.styled';

interface TickerProps {
  description: RichTextBlock[];
  linkLabel?: string;
  link?: {
    url?: string;
    uid?: string;
    target?: string;
  };
}

const Ticker = ({ description, linkLabel, link }: TickerProps): JSX.Element | null => {
  const [isVisible, setIsVisible] = useState(true);
  const tickerRef = useRef<HTMLDivElement>(null);
  const cookieName = 'ticker-dismissed';

  // Generate a simple hash from the description content for unique identification
  const contentHash = description
    ? JSON.stringify(description)
        .split('')
        .reduce((acc, char) => {
          // eslint-disable-next-line no-bitwise
          const hash = (acc << 5) - acc + char.charCodeAt(0);
          // eslint-disable-next-line no-bitwise
          return hash & hash;
        }, 0)
        .toString()
    : '';

  const linkConfig = link ? getLinkConfig(link) : null;
  const hasLink = linkLabel && linkConfig?.url;

  useEffect(() => {
    const dismissedContent = Cookies.get(cookieName);
    if (dismissedContent === contentHash) {
      setIsVisible(false);
    }
  }, [contentHash, cookieName]);

  // Function to measure and set dynamic ticker height
  const updateTickerHeight = () => {
    if (tickerRef.current && isVisible) {
      const height = tickerRef.current.offsetHeight;
      const heightInRem = height / 16; // Convert px to rem (assuming 16px = 1rem)
      document.documentElement.style.setProperty('--ticker-height', `${heightInRem}rem`);
      document.documentElement.style.setProperty('--ticker-height-mobile', `${heightInRem}rem`);
    }
  };

  useEffect(() => {
    // Add/remove body class to indicate ticker presence
    if (isVisible) {
      document.body.classList.add('ticker-visible');
      // Initial height measurement
      updateTickerHeight();

      // Add resize listener to update height on window resize
      const handleResize = () => {
        updateTickerHeight();
      };
      window.addEventListener('resize', handleResize);

      // Use setTimeout to ensure DOM is fully rendered before measuring
      const timeoutId = setTimeout(updateTickerHeight, 100);

      return () => {
        window.removeEventListener('resize', handleResize);
        clearTimeout(timeoutId);
      };
    }
    document.body.classList.remove('ticker-visible');
    // Remove CSS custom properties
    document.documentElement.style.removeProperty('--ticker-height');
    document.documentElement.style.removeProperty('--ticker-height-mobile');

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('ticker-visible');
      document.documentElement.style.removeProperty('--ticker-height');
      document.documentElement.style.removeProperty('--ticker-height-mobile');
    };
  }, [isVisible]);

  const handleClose = () => {
    Cookies.set(cookieName, contentHash, { expires: 30 }); // 30 days
    setIsVisible(false);
  };

  if (!isVisible || !description || description.length === 0) {
    return null;
  }

  return (
    <TickerContainer ref={tickerRef}>
      <TickerContent>
        <TickerMessage>
          <RichText render={description} />
        </TickerMessage>
        {hasLink && (
          <TickerLink
            url={linkConfig.url}
            title={linkLabel}
            isExternal={linkConfig.isExternal}
            target={link?.target}
          >
            {linkLabel}
          </TickerLink>
        )}
        <CloseButton onClick={handleClose} aria-label="Close ticker">
          ×
        </CloseButton>
      </TickerContent>
    </TickerContainer>
  );
};

export default Ticker;
